import { Component, inject } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbAccordionModule, NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FieldType, FormlyFormBuilder, FormlyModule } from '@ngx-formly/core';
import { HotToastService } from '@ngxpert/hot-toast';
import JSON5 from 'json5';
import { Subscription } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

import { Lookup } from '@shared/interfaces/lookup';
import { ModalParam } from '@shared/interfaces/modal-param';
import { ConfirmModalComponent } from '@shared/modals/confirm-modal/confirm-modal.component';
import { SasDynamicFormService } from '@shared/sas-dynamic-form/sas-dynamic-form.service';
import { LookupUseCacheService } from '@shared/services/lookup-use-cache.service';

const READ_MODE_CSSCLASS = 'textmode';
const DELETE = 'delete';

@Component({
    selector: 'sas-repeat-section',
    standalone: true,
    imports: [ReactiveFormsModule, FormlyModule, NgbAccordionModule, NgbTooltipModule, TranslatePipe],
    templateUrl: './sas-repeat-section.component.html',
    styleUrl: './sas-repeat-section.component.scss'
})
export class SasRepeatSectionComponent extends FieldType {
    private sasDynamicFormService = inject(SasDynamicFormService);
    private modalService = inject(NgbModal);
    private notification = inject(HotToastService);
    private formlyBuilder = inject(FormlyFormBuilder);
    private lookupUseCacheService = inject(LookupUseCacheService);

    isReadMode: boolean = false;
    private fields: Array<any> = [];
    fieldCopies: Map<number, Array<any>> = new Map();
    formArray: FormArray<FormGroup<any>> = new FormArray<FormGroup<any>>([]);
    private unique: number = 0;
    allowCollapse: boolean = false;
    /**
     * Tracks active subscriptions for the "title" control of each repeated section row.
     */
    private titleSubscriptions: Map<number, Subscription> = new Map();
    titles: Array<string> = [];

    constructor() {
        super();
    }

    ngOnInit() {
        this.isReadMode = this.formState.readonly ?? false;
        this.fields = this.field.props?.['fields'] ?? [];
        this.lookupUseCacheService.isUsingCache = this.field.props?.['useCache'] ?? false;
        this.allowCollapse = this.field.props?.['allowCollapse'] ?? false;

        const { key } = this.field;
        const items = this.model[`${key}`] ?? [];

        this.formArray = new FormArray(items.map(() => new FormGroup({})));

        if (this.form && this.form.get(`${key}`) !== this.formArray) {
            (this.form as FormGroup).setControl(`${key}`, this.formArray);
        }

        (this.field as any).formControl = this.formArray;

        items.forEach((item: any, index: any) => {
            this.getCachedFields(index);
            const { fields } = this.getFormAndFieldsByIndex(index);

            this.titles[index] = this.getRepeatTitle(fields, item);
        });
    }

    getKey(field: any): string {
        return field.key;
    }

    getCachedFields(index: number): Array<any> {
        if (!this.fieldCopies.has(index)) {
            const copiedFields = this.copyFields(this.fields);

            this.fieldCopies.set(index, copiedFields);
        }

        return this.fieldCopies.get(index)!;
    }

    private copyFields(fields: Array<any>) {
        let newFields = fields.map((field) => this.sasDynamicFormService.mapFieldSchema(field));

        newFields = this.addRandomIds(newFields);

        if (this.isReadMode) {
            this.setReadMode(newFields);
        }

        return newFields;
    }

    onSectionToggle(index: number, isOpen: boolean) {
        if (isOpen) {
            const { form, fields } = this.getFormAndFieldsByIndex(index);

            if (!this.titleSubscriptions.has(index)) {
                const sub$ = form.valueChanges.subscribe(model => {
                    this.titles[index] = this.getRepeatTitle(fields, model);
                });
                this.titleSubscriptions.set(index, sub$);
            }
            return;
        }

        this.unsubscribeTitle(index);
    }

    private getFormAndFieldsByIndex(index: number) {
        const form = this.formArray.at(index);
        const fields = this.fieldCopies.get(index)!;

        return { form, fields };
    }

    private getRepeatTitle(fields: Array<any>, model: any): string {
        const titleFields: Array<string> = this.field.props?.['titleFields'] ?? [];
        let title = '';

        if (titleFields.length !== 0) {
            titleFields.forEach(fieldKey => {
                const field = fields.find(field => field.key === fieldKey);

                const isDropdown = field !== undefined && field.type === 'sasSelect';
                const titleValue = this.getTitle({ field, fieldKey, model, isDropdown });

                if (titleValue.length > 0) {
                    title += `${titleValue}, `;
                }
            });
        }

        if (title.endsWith(', ')) {
            title = title.slice(0, -2);
        }

        if (title.length > 120) {
            return `${title.slice(0, 120)}...`;
        }

        return title;
    }

    private getTitle({ field, fieldKey, model, isDropdown }: { field: any, fieldKey: string, model: any, isDropdown: boolean }): string {
        const modelValue = model[fieldKey];

        if (isDropdown) {
            let selectedValue = field.props.sasOptions.find((option: Lookup) => option.id === modelValue);

            if (!selectedValue && field.props['dataSource']?.type === 'static') {
                const options = JSON5.parse(field.props['dataSource'].source);

                selectedValue = options.find((option: Lookup) => option.id === modelValue);
            }

            return selectedValue ? selectedValue.name : '';
        }

        if (modelValue) {
            if (Array.isArray(modelValue)) {
                return modelValue.map(v => v.name).join(', ');
            }

            if (modelValue.length > 0) {
                return modelValue;
            }
        }

        return '';
    }

    /**
     * Unsubscribes from and removes the title subscription associated with the given index.
     *
     * This helps prevent memory leaks and dangling subscriptions when repeat-section items are
     * removed or otherwise no longer needed.
     *
     * @param index - The index identifying the subscription in the `titleSubscriptions` map.
     */
    private unsubscribeTitle(index: number): void {
        const sub$ = this.titleSubscriptions.get(index);
        if (sub$) {
            sub$.unsubscribe();
            this.titleSubscriptions.delete(index);
        }
    }

    addNew() {
        const { key } = this.field;
        this.model[`${key}`] = this.model[`${key}`] || [];

        const lastSection = this.model[`${key}`][this.model[`${key}`].length - 1];
        let newSection: any = {};

        if (lastSection) {
            newSection = structuredClone(lastSection);

            for (const prop in newSection) {
                newSection[prop] = this.setEmpty(newSection[prop]);
            }
        }

        this.model[`${key}`].push(newSection);
        this.formArray.push(new FormGroup({}));
        this.formlyBuilder.buildForm(
            this.formArray.at(this.formArray.length - 1),
            this.getCachedFields(this.formArray.length - 1),
            this.field.model,
            {}
        );
    }

    private addRandomIds(fields: Array<any>) {
        this.unique++;

        fields.forEach((field: any, index: number) => {
            if (field.fieldGroup) {
                field.fieldGroup = this.addRandomIds(field.fieldGroup);
                return; // fieldGroups don't need an ID
            }

            if (field.props?.fields) {
                field.props.fields = this.addRandomIds(field.props.fields);
            }

            field.id = field.id || `${field.key}_${index}_${this.unique}${this.getRandomInt(0, 9999)}`;

            field.name = field.id; // overwrite the name as on repetition we cannot allow repeated names
        });

        return fields;
    }

    private getRandomInt(min: number, max: number) {
        return Math.floor(Math.random() * (max - min)) + min;
    }

    private setReadMode(fields: Array<any>) {
        fields.forEach((field: any) => {
            if (field.fieldGroup) {
                field.fieldGroup = this.setReadMode(field.fieldGroup);

                return;
            }

            if (field.props?.fields) {
                field.props.fields = this.setReadMode(field.props.fields);
            }

            if (field.props.sasClass === undefined) {
                field.props.sasClass = '';
            }

            const currentClass = field.templateOptions?.sasClass || '';

            field.templateOptions = {
                ...field.templateOptions,
                sasClass: `${currentClass} ${READ_MODE_CSSCLASS}`.trim()
            };
        });

        return fields;
    }

    private setEmpty(value: any) {
        switch (value) {
            case typeof value === 'undefined':
                return undefined;
            case value === null:
                return null;
            case typeof value === 'boolean':
                return false;
            case typeof value === 'number' && isFinite(value):
                return 0;
            case typeof value === 'string' || value instanceof String:
                return '';
            case value && typeof value === 'object' && value.constructor === Object:
                return null;
            default:
                return undefined;
        }
    }

    removeSection(key: any, index: number) {
        const actionParams: ModalParam = {
            title: 'Delete section',
            message: 'Are you sure you want to delete the section? Any information entered will be lost.',
            action: DELETE
        };

        this.openConfirm(actionParams, key, index);
    }

    private openConfirm(actionParams: ModalParam, key: any, index: number) {
        const modal = this.modalService.open(ConfirmModalComponent, { size: 'lg', centered: true });
        modal.componentInstance.params = actionParams;

        modal.result.then(
            (result) => {
                if (result.confirmed) {
                    if (key && index > -1) {
                        this.model[`${key}`].splice(index, 1);
                        this.formArray.removeAt(index);

                        if (this.allowCollapse) {
                            this.titles.splice(index, 1);
                            this.unsubscribeTitle(index);
                        }

                        this.fieldCopies.delete(index);

                        this.rebuildFieldCopies();
                    }

                    this.notification.success('Section removed!');
                }
            },
            (reason) => { } // Dismissed, not show nothing
        );
    }

    private rebuildFieldCopies() {
        this.fieldCopies.clear();
        const { key } = this.field;

        this.model[`${key}`].forEach((_: any, index: number) => {
            this.fieldCopies.set(index, this.copyFields(this.fields));
        });
    }

    ngOnDestroy() {
        this.formArray.clear();
        this.fieldCopies.clear();
        this.titleSubscriptions.forEach(sub => sub.unsubscribe());
    }
}
